import { logInfo } from "common/debug-print";
import { defined, sleep } from "common/Support";
import { getCredentials } from "webapp/lib/common-google-api";

import { loadPageJavascript } from "./browser-hacks";

export { getCredentials } from "webapp/lib/common-google-api";

export const googleDriveScope = "https://www.googleapis.com/auth/drive.file";

let initPromise: Promise<void> | undefined;
let auth2Instance: gapi.auth2.GoogleAuth | undefined;
let initialized = false;

const waitTime = 500;
const maxInitWaitIterations = 40; // We sleep for 500 ms waiting for gapi to show up.
// 40 * 500 ms = 20 seconds waiting around for gapi.
// If we don't get it within 20 seconds then we assume we're never getting it.
// At one point we bumped this down to 10 seconds but some of our users have
// extremely poor network connections, and this still isn't enough time.
const maxWaitTime = 2 * maxInitWaitIterations * waitTime;

let gapiLoaded = false;
(window as any).gapiInitializedCallback = () => {
    gapiLoaded = true;
};

async function waitForGapi(): Promise<boolean> {
    let waits = 0;
    while (gapiLoaded === false && waits < maxInitWaitIterations) {
        await sleep(waitTime);
        waits++;
    }

    return gapiLoaded;
}

async function doInit(): Promise<void> {
    logInfo("initing google api");
    // As of 2020-02-17, this is the official entrypoint for Google APIs.
    // See https://github.com/google/google-api-javascript-client/issues/399#issuecomment-569415878
    // as to why we have to inject a callback name.
    loadPageJavascript("https://apis.google.com/js/platform.js?onload=gapiInitializedCallback");

    if (!(await waitForGapi())) return;

    const loadedFlag = "loaded";
    const loadPromise = new Promise<typeof loadedFlag>((resolve, reject) => {
        let waits = 0;

        gapi.load("client:picker", {
            callback: async () => {
                // gapi.client is not always ready when this fires.
                // See https://github.com/google/google-api-javascript-client/issues/399
                while (gapi.client === undefined && waits < maxInitWaitIterations) {
                    await sleep(waitTime);
                    waits++;
                }
                if (gapi.client === undefined) {
                    reject(new Error("Google API client was not installed"));
                    return;
                }
                // Even though .load is a Then'able, this sometimes interacts extremely poorly with
                // Promise support. It's better to up-convert to a proper Promise whenever possible.
                (gapi.client as any)
                    .load("https://www.googleapis.com/discovery/v1/apis/drive/v3/rest")
                    .then(() => resolve(loadedFlag))
                    .catch(reject);
            },
            onerror: (err: any) => reject(err),
        });
    });
    // There are many, many things that could have gone wrong in the process of just setting up
    // the Google API. We have to establish a timeout at some point, and that timeout is 20 seconds.
    // We can't use withTimeout here because we want to throw the actual error involved;
    // this will bubble up to consumers, who will ultimately pop a modal showing said error.
    const loadResult = await Promise.race([loadPromise, (() => sleep(maxWaitTime).then(() => "timeout"))()]);
    if (loadResult !== loadedFlag) {
        throw new Error("Timed out trying to load Google Drive support");
    }

    const creds = getCredentials();
    // Even though .init is a Then'able, this sometimes interacts extremely poorly with
    // Promise support. It's better to up-convert to a proper Promise whenever possible.
    const clientPromise = new Promise((resolve, reject) =>
        gapi.client
            .init({
                apiKey: creds.developerKey,
                clientId: creds.clientId,
                discoveryDocs: creds.discoveryDocs,
                scope: "email",
            })
            .then(() => resolve(loadedFlag))
            .catch(reject)
    );
    const clientResult = await Promise.race([clientPromise, sleep(maxWaitTime).then(() => "timeout")]);
    if (clientResult !== loadedFlag) {
        throw new Error(
            "Timed out trying to initialize Google APIs. This is usually caused by a browser plugin (e.g. ad blockers), blocking the connection to Google."
        );
    }

    initialized = true;
}

export async function init(): Promise<void> {
    if (initPromise === undefined) {
        initPromise = doInit();
    }
    return initPromise;
}

export async function getGoogleAsync(): Promise<typeof google | undefined> {
    await init();
    if (!initialized) return undefined;
    return (window as any).google;
}

export async function getGAPIAsync(): Promise<typeof gapi | undefined> {
    await init();
    if (!initialized) return undefined;
    return (window as any).gapi;
}

// @ts-ignore
export async function getAuth2(): Promise<gapi.auth2.GoogleAuth | undefined> {
    await init();
    if (!initialized) return undefined;
    auth2Instance = gapi.auth2.getAuthInstance();
    return defined(auth2Instance) as any;
}

export async function setGAPIAccessToken(accessToken: string): Promise<void> {
    const gapi = await getGAPIAsync();
    if (gapi === undefined) return;
    logInfo("setting GAPI access token", accessToken);
    gapi.client.setToken({ access_token: accessToken });
}

export const googleConnectionFailureMessage = "Could not connect to Google.";
